import { getCaptionString, Translation } from '@modeso/aumico-lib-core-fe';
import { EMPTY_STRING } from '@modeso/aumico-lib-settings-fe';
import { isFli, isGroup, isSubfli } from '@modeso/aumico-lib-templates-fe';
import {
    AccountMappingAccountItem,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroup,
    IGroupedAccountDefition,
    IGroupingMasking,
    ITranslationEntry,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ItemType } from '@modeso/types__aumico-lib-templates-be';
import { isAccount } from '../../../../shared/util/accountTypeGuard';
import { isSubTotal } from '../../../../../../../aumico-lib-templates-fe/src/lib/type-guards/type-guards';
import { GatheringItems } from './GatheringVisitor';
import { GapReportingVisitor } from './GapReportingVisitor';
import { ITranslatorVisitor } from './ITranslatorVisitor.interface';
import { UntranslatedTexts } from './UntranslatedTexts';
import { Group } from './classes/Group';
import { SortedItem } from './classes/SortedGroup';
import { Template } from './classes/Template';

export class TemplateTranslatorVisitor extends GapReportingVisitor implements ITranslatorVisitor {
    // The sorted tree and the group tree reference the same positions, so `translate` reaches each
    // item twice. Resolve/report only once per item — a second pass would read the already-inserted
    // translation as the source and report it as a fresh gap.
    private readonly seenItems = new WeakSet<object>();

    constructor(
        private readonly targetLanguage: string,
        private readonly translatableItems: Map<string, Translation>,
        private readonly reportLanguage: string,
        untranslated?: UntranslatedTexts,
        private readonly groupingAndMasking?: IGroupingMasking | null,
        translations?: Record<string, ITranslationEntry>,
        private readonly reportsReportWideNames: boolean = true,
    ) {
        super(untranslated, 'template', translations);
    }

    visit(template: Template): void {
        if (this.reportsReportWideNames) {
            this.reportRenamedAndGroupedAccounts();
        }

        const gatheringVisitor = new GatheringItems();
        this.gatherSortedItems(gatheringVisitor, template.group.sortedItems as SortedMappingItem[]);
        this.gatherGroupsAndItems(gatheringVisitor, template.group);
    }

    private translate(itemsToTranslate: (SortedMappingItem | IAccountMappingSubFLI)[]): void {
        itemsToTranslate.forEach((sortedItem) => {
            if (this.seenItems.has(sortedItem)) {
                return;
            }
            this.seenItems.add(sortedItem);
            if (isGroup(sortedItem)) {
                this.keepOriginalTitle(sortedItem);
            }

            const key = this.getKey(sortedItem);
            const translations = this.translatableItems.get(key);

            if (translations != null) {
                const translationInTargetLanguage = translations[this.targetLanguage] ?? EMPTY_STRING;
                if (isGroup(sortedItem)) {
                    this.translateGroup(sortedItem, translationInTargetLanguage, key);
                } else if (isSubTotal(sortedItem)) {
                    sortedItem.caption = this.resolve(
                        getCaptionString(sortedItem.caption, this.reportLanguage),
                        translationInTargetLanguage,
                    );
                } else if (isFli(sortedItem)) {
                    sortedItem.caption = this.resolve(
                        getCaptionString(sortedItem.caption, this.reportLanguage),
                        translationInTargetLanguage,
                    );
                    sortedItem.accounts.forEach((child) => {
                        if (isSubfli(child)) {
                            // Also a top-level entry in the sorted list — mark it so that pass skips it.
                            this.seenItems.add(child);
                            const key = this.getKey(child);
                            const translations = this.translatableItems.get(key);
                            if (translations != null) {
                                child.caption = this.resolve(
                                    getCaptionString(child.caption, this.reportLanguage),
                                    translations[this.targetLanguage] ?? EMPTY_STRING,
                                );
                            } else {
                                this.reportSelfCreated(child);
                            }
                        }
                    });
                }
            } else {
                this.reportSelfCreated(sortedItem);
            }
            this.reportAccounts(sortedItem);
            // REV-21347: even when no translation entry exists, a caption/title that is still stored
            // in the pre-migration locale-object shape must not leak to the print output as
            // "[object Object]" — resolve it to the target-language string.
            this.coerceObjectCaption(sortedItem);
        });
    }

    /** Account names come from the bookkeeping; nothing to compare against, and always the stored value. */
    private reportAccounts(sortedItem: SortedMappingItem | IAccountMappingSubFLI): void {
        if (!isFli(sortedItem) && !isSubfli(sortedItem)) {
            return;
        }

        // A SubFLI carries IAccount[] while an FLI carries the wider union, so the element type is
        // widened once here instead of narrowing twice.
        sortedItem.reclassesAccounts?.forEach((account) => this.translateAccountName(account));

        (sortedItem.accounts as AccountMappingAccountItem[]).forEach((child) => {
            if (isSubfli(child)) {
                child.accounts.forEach((account) => this.translateAccountName(account));
                child.reclassesAccounts?.forEach((account) => this.translateAccountName(account));
            } else if (isAccount(child)) {
                this.translateAccountName(child);
            } else if (child.type === ItemType.HEADER) {
                child.caption = this.resolve(getCaptionString(child.caption, this.reportLanguage));
            }
        });
    }

    /**
     * Grouping, masking and sorting recognise an account by a key built from its NAME, and that key
     * was stored with the report-language one. Keeping the original here lets them keep matching
     * after the name has been translated.
     */
    private translateAccountName(account: { name?: string | null; originalName?: string | null }): void {
        if (account.originalName == null) {
            account.originalName = account.name ?? '';
        }
        account.name = this.resolve(account.name, undefined, 'accounts');
    }

    /**
     * The same for a group's title: grouping, sorting and hidden subtotals address a position by a
     * path built from the titles above it (`GroupingPathHelper.getGroupPath`), stored with the
     * report-language ones — "Aktiven", not "Assets".
     */
    private keepOriginalTitle(group: IAccountMappingTemplateGroup & { originalTitle?: unknown }): void {
        if (group.originalTitle === undefined) {
            group.originalTitle = group.title;
        }
    }

    /** Renames and group names are the user's own — flat records, no tree walk. Runs once per template. */
    private reportRenamedAndGroupedAccounts(): void {
        // On the clone's grouping/masking, so filling is safe — the stored config is untouched.
        const renamedAccounts = this.groupingAndMasking?.renamedAccounts ?? {};
        Object.keys(renamedAccounts).forEach(
            (key) => (renamedAccounts[key] = this.resolve(renamedAccounts[key], undefined, 'accounts')),
        );
        // The saved sort order knows a group row by its report-language name; the summary row carries
        // it on as `originalName` (GroupingHelper.applyGroupingAndSummarize).
        Object.values(this.groupingAndMasking?.groupedAccounts ?? {}).forEach(
            (group: IGroupedAccountDefition & { originalGroupName?: string }) => {
                if (group.originalGroupName == null) {
                    group.originalGroupName = group.groupName;
                }
                group.groupName = this.resolve(group.groupName, undefined, 'accounts');
            },
        );
    }

    private reportSelfCreated(sortedItem: SortedMappingItem | IAccountMappingSubFLI): void {
        if (isGroup(sortedItem)) {
            const title = getCaptionString(sortedItem.title, this.reportLanguage);
            sortedItem.title = this.resolve(title);
            const totalName = getCaptionString(sortedItem.totalName, this.reportLanguage);
            sortedItem.totalName = this.resolve(totalName);
        } else if (isSubTotal(sortedItem) || isFli(sortedItem) || isSubfli(sortedItem)) {
            const caption = getCaptionString(sortedItem.caption, this.reportLanguage);
            sortedItem.caption = this.resolve(caption);
        }
    }

    /** Resolve any locale-object caption/title left on an item (or its SubFLIs) to a plain string. */
    private coerceObjectCaption(sortedItem: SortedMappingItem | IAccountMappingSubFLI): void {
        if (isGroup(sortedItem)) {
            sortedItem.title = getCaptionString(sortedItem.title, this.targetLanguage);
            if (sortedItem.totalName != null) {
                sortedItem.totalName = getCaptionString(sortedItem.totalName, this.targetLanguage);
            }
        } else if (isSubTotal(sortedItem)) {
            sortedItem.caption = getCaptionString(sortedItem.caption, this.targetLanguage);
        } else if (isFli(sortedItem)) {
            sortedItem.caption = getCaptionString(sortedItem.caption, this.targetLanguage);
            sortedItem.accounts.forEach((child) => {
                if (isSubfli(child)) child.caption = getCaptionString(child.caption, this.targetLanguage);
            });
        } else if (isSubfli(sortedItem)) {
            sortedItem.caption = getCaptionString(sortedItem.caption, this.targetLanguage);
        }
    }

    private getKey(sortedItem: SortedMappingItem | IAccountMappingSubFLI): string {
        if (isGroup(sortedItem)) {
            return `${sortedItem.name}_${ItemType.GROUP}`;
        } else {
            return `${sortedItem.name}_${sortedItem.type}`;
        }
    }

    private gatherSortedItems(gatheringVisitor: GatheringItems, sortedItems: SortedMappingItem[]) {
        sortedItems.forEach((sortedItem) => new SortedItem(sortedItem).accept(gatheringVisitor));

        const sortedItemsToTranslate = gatheringVisitor.sortedItemsToTranslate;

        this.translate(sortedItemsToTranslate);
    }

    private gatherGroupsAndItems(gatheringVisitor: GatheringItems, group: IAccountMappingTemplateGroup) {
        new Group(group).accept(gatheringVisitor);
        const groupsToTranslate = gatheringVisitor.groupsToTranslate;
        const itemsToTranslate = gatheringVisitor.itemsToTranslate;
        this.translate(groupsToTranslate);
        this.translate(itemsToTranslate);
    }

    private translateGroup(group: IAccountMappingTemplateGroup, translationInTargetLanguage: string, key: string) {
        group.title = this.resolve(getCaptionString(group.title, this.reportLanguage), translationInTargetLanguage);
        const nameOfTotal = `${key}_total`;
        const translations = this.translatableItems.get(nameOfTotal);
        const totalTranslationInTargetLanguage = translations?.[this.targetLanguage];
        group.totalName = this.resolve(
            getCaptionString(group.totalName, this.reportLanguage),
            totalTranslationInTargetLanguage ?? '',
        );
    }
}
